import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";
import { Patient } from "../patient.model";

interface GeneralAppearanceFields {
  /**
   * The patient's level of consciousness.
   *
   * Options: lethargic or somnolent.
   */
  loc: string;

  /**
   * The patient's sense of orientation.
   *
   * Options: not to person, not to place, not to time, and not to situation.
   */
  orientation: string[];

  /**
   * Whether the patient is letharic (true) or not (false).
   */
  lethargic: boolean;
  inappropriateForAge: boolean;
  distress: boolean;

  // Ob only.
  babyBonding: string;
  hasBreastAbnormalities: boolean;
  nippleAbnormalitiesNotes: string;
  breastAbnormalitiesNotes: string;
}

export type GeneralAppearanceFormGroup = FormGroup<ControlsOf<GeneralAppearanceFields>>;

export const GeneralAppearanceAssessmentHelpers: AssessmentHelpers = {
  createFormGroup: createGeneralAppearanceFormGroup,
  toPDF: generalAppearanceAssessmentToPDF,
  formToObject: formToGeneralAppearanceAssessment,
  initialState: {
    loc: '', // lethargic, somnolent
    orientation: [],
    lethargic: false,
    inappropriateForAge: false,
    distress: false,
    babyBonding: false,
    hasBreastAbnormalities: false,
    nippleAbnormalitiesNotes: '',
    breastAbnormalitiesNotes: '',
  },
};

/**
 * Holds information about the patient's general appearance assessment.
 */
export class GeneralAppearanceAssessment {
  loc: string;
  orientation: string[];
  lethargic: boolean;
  inappropriateForAge: boolean;
  distress: boolean;
  babyBonding: string;
  hasBreastAbnormalities: boolean;
  nippleAbnormalitiesNotes: string;
  breastAbnormalitiesNotes: string;

  constructor(data: Partial<GeneralAppearanceFields>) {
    const is = GeneralAppearanceAssessmentHelpers.initialState;
    this.loc = data.loc || is.loc;
    this.orientation = data.orientation || is.orientation;
    this.lethargic = data.lethargic ?? is.lethargic;
    this.inappropriateForAge = data.inappropriateForAge ?? is.inappropriateForAge;
    this.distress = data.distress ?? is.distress;
    this.babyBonding = data.babyBonding ?? is.babyBonding;
    this.hasBreastAbnormalities = data.hasBreastAbnormalities ?? is.hasBreastAbnormalities;
    this.nippleAbnormalitiesNotes = data.nippleAbnormalitiesNotes || is.nippleAbnormalitiesNotes;
    this.breastAbnormalitiesNotes = data.breastAbnormalitiesNotes || is.breastAbnormalitiesNotes;
  }
}


function createGeneralAppearanceFormGroup(generalAppearanceAssessment?: GeneralAppearanceAssessment) {
  const data = generalAppearanceAssessment || new GeneralAppearanceAssessment(GeneralAppearanceAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<GeneralAppearanceFields>>({
    loc: new FormControl<string | null>(data.loc),
    orientation: new FormControl<string[] | null>(data.orientation),
    lethargic: new FormControl<boolean | null>(data.lethargic),
    inappropriateForAge: new FormControl<boolean | null>(data.inappropriateForAge),
    distress: new FormControl<boolean | null>(data.distress),
    babyBonding: new FormControl<string | null>(data.babyBonding),
    hasBreastAbnormalities: new FormControl<boolean | null>(data.hasBreastAbnormalities),
    nippleAbnormalitiesNotes: new FormControl<string | null>(data.nippleAbnormalitiesNotes),
    breastAbnormalitiesNotes: new FormControl<string | null>(data.breastAbnormalitiesNotes),
  });
}

function generalAppearanceAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: GeneralAppearanceAssessment, patient: Patient) {
  pdf.addSectionTitle("General Appearance");
  pdf.pdf.setFont("Helvetica", "normal");
  pdf.addTwoColumnText("LOC", assessment.loc,
    "Orientation", assessment.orientation.join(", "));
  pdf.addTwoColumnText("Lethargic?", assessment.lethargic ? "Yes" : "No",
    "Inappropriate for Age?", assessment.inappropriateForAge ? "Yes" : "No");
  pdf.addTwoColumnText("In Distress?", assessment.distress ? "Yes" : "No", "", "");
  if (patient.isObstetric()) {
    pdf.addOneColumnText("Not Bonding with Baby Appropriately", assessment.babyBonding ? "Yes" : "No");
    pdf.addTwoColumnText("Nipple Abnormalities", assessment.nippleAbnormalitiesNotes,
      "Breast Abnormalities", assessment.breastAbnormalitiesNotes)
  }
}

function formToGeneralAppearanceAssessment(fg: GeneralAppearanceFormGroup) {
  return new GeneralAppearanceAssessment({
    loc: fg.controls.loc.value!,
    orientation: fg.controls.orientation.value!,
    lethargic: fg.controls.lethargic.value!,
    inappropriateForAge: fg.controls.inappropriateForAge.value!,
    distress: fg.controls.distress.value!,
    babyBonding: fg.controls.babyBonding.value!,
    hasBreastAbnormalities: fg.controls.hasBreastAbnormalities.value!,
    nippleAbnormalitiesNotes: fg.controls.nippleAbnormalitiesNotes.value!,
    breastAbnormalitiesNotes: fg.controls.breastAbnormalitiesNotes.value!,
  });
}